import Joi from 'joi';
import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import useSubscriber, { S3Media, S3MediaSchema } from './useSubscriber';

export const arrayOnlyStrugglesTtsProviders = ['onlystruggles', 'fishaudio', 'elevenlabs'] as const;
export type OnlyStrugglesTtsProviders = typeof arrayOnlyStrugglesTtsProviders[number];

export interface TtsReferenceModel {
    _id: string;
    subscriberId: string;
    name: string;
    description?: string;
    audioFiles: S3Media[];
    provider: OnlyStrugglesTtsProviders;
    modelId: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export const TtsReferenceModelSchema = Joi.object<TtsReferenceModel>({
    _id: Joi.string().required(),
    subscriberId: Joi.string().required(),
    name: Joi.string().required(),
    description: Joi.string().optional(),
    audioFiles: Joi.array().items(S3MediaSchema).required(),
    provider: Joi.string()
        .valid(...arrayOnlyStrugglesTtsProviders)
        .required(),
    modelId: Joi.string().required(),
    createdAt: Joi.date().optional(),
    updatedAt: Joi.date().optional(),
});

const useTtsReferenceModels = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const dialog = useDialog();
    const axios = useAxios();

    const [ttsGenerateTestLoading, setTtsGenerateTestLoading] = useState<boolean>(false);
    const [ttsTrainModelLoading, setTtsTrainModelLoading] = useState<boolean>(false);
    const [ttsDeleteModelLoading, setTtsDeleteModelLoading] = useState<boolean>(false);

    // Fetch Subscriber's Mass DM
    const fetchData = async (): Promise<TtsReferenceModel[] | null> => {
        const query: string = `${settingsContext.routes.tts.base}${subscriber._id}/referenceModel/list`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return null;
            });
    };

    const ttsReferenceModels = useQuery(['ttsReferenceModels', subscriber?._id], () => fetchData(), {
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        enabled: userContext.jwtToken && settingsContext.apiKey && subscriber && subscriber._id ? true : false,
    });

    // Train a new TTS reference model
    const ttsTrainModel = async (
        name: string,
        description: string | undefined | null,
        audioFiles: File[],
    ): Promise<TtsReferenceModel | null> => {
        const query: string = `${settingsContext.routes.tts.base}${subscriber._id}/referenceModel/train`;

        const formData = new FormData();

        setTtsTrainModelLoading(true);
        formData.append('name', name);
        formData.append('description', description || '');
        formData.append('audioFiles', audioFiles[0]);

        return axios
            .post(query, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then(response => {
                setTtsTrainModelLoading(false);

                if (response.status !== 200) {
                    console.error('Error training TTS model:', response.data);

                    return null;
                }

                return response.data;
            })
            .catch(error => {
                setTtsTrainModelLoading(false);

                console.error('Error training TTS model:', error);

                handleHttpError(error, dialog);

                return null;
            });
    };

    // Delete a TTS reference model
    const deleteModel = async (modelId: string): Promise<void> => {
        const query: string = `${settingsContext.routes.tts.base}${subscriber._id}/referenceModel/delete/${modelId}`;

        setTtsDeleteModelLoading(true);
        return axios
            .delete(query)
            .then(response => {
                setTtsDeleteModelLoading(false);

                if (response.status !== 200) {
                    console.error('Error deleting TTS model:', response.data);
                    handleHttpError(response.data, dialog);
                    return;
                }

                // Refetch the models after deletion
                ttsReferenceModels.refetch();
            })
            .catch(error => {
                setTtsDeleteModelLoading(false);
                console.error('Error deleting TTS model:', error);
                handleHttpError(error, dialog);
            });
    };

    const ttsGenerateTest = async (
        modelId: string,
        prompt: string,
        provider: OnlyStrugglesTtsProviders,
        elevenLabsApiKey?: string | null,
    ): Promise<{ success: boolean; message: string; url: string; fileName: string } | null> => {
        setTtsGenerateTestLoading(true);

        const query: string = `${settingsContext.routes.tts.base}${subscriber._id}/referenceModel/generateTestTts/${modelId}`;
        return axios
            .post(query, { prompt, provider, elevenLabsApiKey })
            .then(response => {
                setTtsGenerateTestLoading(false);

                if (response.status !== 200) {
                    console.error('Error generating TTS test:', response.data);

                    return null;
                }

                return response.data;
            })
            .catch(error => {
                setTtsGenerateTestLoading(false);

                console.error('Error generating TTS test:', error);

                handleHttpError(error, dialog);

                return null;
            });
    };

    return {
        ...ttsReferenceModels,
        ttsGenerateTest,
        ttsGenerateTestLoading,
        ttsTrainModel,
        ttsTrainModelLoading,
        ttsDeleteModelLoading,
        deleteModel,
    };
};

export default useTtsReferenceModels;
